import { TrashBaseIcon } from '@tableau/icons/TrashBaseIcon';
import { ButtonType, ButtonWidget, DropdownButtonType, List, SingleSelectDropdown, TextFieldWidget } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { dropdownListStyle } from '~src/settings/styles/CommonStyles';

import { EXTENSION_PACKAGE_REST_ALLOWLIST_VERBS, ExtensionPackageRestAllowlistCodec, ExtensionPackageRestAllowlistEntry, ExtensionPackageRestAllowlistViolation } from '../utils/ExtensionPackageRestAllowlistCodec';

export const EXTENSION_PACKAGE_REST_ALLOWLIST_LIST_TEST_ID = 'extension-package-rest-allowlist-list';
export const EXTENSION_PACKAGE_REST_ALLOWLIST_ADD_BUTTON_TEST_ID = 'extension-package-rest-allowlist-add-button';
export const EXTENSION_PACKAGE_REST_ALLOWLIST_ROW_TEST_ID_PREFIX = 'extension-package-rest-allowlist-row';
export const EXTENSION_PACKAGE_REST_ALLOWLIST_DELETE_BUTTON_TEST_ID_PREFIX = 'extension-package-rest-allowlist-delete-button';
export const EXTENSION_PACKAGE_REST_ALLOWLIST_VERB_TEST_ID_PREFIX = 'extension-package-rest-allowlist-verb';
// Common path prefix prefilled into new rows (see handleAdd).
export const EXTENSION_PACKAGE_REST_ALLOWLIST_PATH_PREFIX = '/{api-version}/sites/{site-id}/';

interface ExtensionPackageRestAllowlistListViewProps {
  /** In-progress entries as the admin has edited them -- not necessarily all valid. */
  readonly entries: ExtensionPackageRestAllowlistEntry[];
  readonly onChange: (entries: ExtensionPackageRestAllowlistEntry[]) => void;
  readonly disabled?: boolean;
}

const verbItems = EXTENSION_PACKAGE_REST_ALLOWLIST_VERBS.map(verb => ({ content: verb, value: verb, testId: verb }));

// Lay each entry out as a single row: the url-path field fills the available width, with the
// narrow HTTP-verb dropdown and the delete button to its right. `flex-start` keeps the dropdown and
// button aligned to the field's input line even when an invalid-path message pushes the row taller.
// No flex `gap` -- unsupported on our IE11 target -- so spacing is done with margins.
const entryRowStyle = style({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: CommonStyles.Sizes.DefaultSpace
});
const urlCellStyle = style({
  // `0%` basis (not `auto`) so the cell width comes from flex distribution rather than the input's
  // content -- otherwise the field grows as you type and shoves the delete button past the panel
  // edge. `0%` (rather than `0`) keeps IE11 happy. `minWidth: 0` lets it shrink below content width.
  flex: '1 1 0%',
  minWidth: 0
});
const urlFieldStyle = style({
  width: '100%',
  $nest: { input: { width: '100%' } }
});
const verbDropdownStyle = style({
  flex: '0 0 auto',
  width: 120,
  marginLeft: CommonStyles.Sizes.DefaultSpace
});
// Separate the path list from the "Allow all" checkbox above it so the two controls read as
// distinct groups rather than crowding together.
const listContainerStyle = style({
  marginTop: CommonStyles.Sizes.MediumSpace
});

// Maps a validation-failure category to the specific field message shown under the row.
function messageForViolation(violation: ExtensionPackageRestAllowlistViolation | undefined): string {
  switch (violation) {
    case 'invalidPathChar':
      return Messages.extensionPackageRestAllowlistInvalidPathCharMessage();
    case 'invalidPlaceholder':
      return Messages.extensionPackageRestAllowlistInvalidPlaceholderMessage();
    case 'urlTooLong':
      return Messages.extensionPackageRestAllowlistUrlTooLongMessage();
    default: // 'notRelative' and the UI-unreachable 'unsupportedVerb'
      return Messages.extensionPackageRestAllowlistInvalidUrlMessage();
  }
}

/**
 * Controlled list editor for the `extensionPackageRestAllowlist` site setting: one row per {url, verb}
 * entry, each a url-path text field plus an HTTP-verb dropdown, with add/delete rows and inline
 * per-row validity feedback. Purely controlled by `entries`/`onChange` -- it owns no fetch/save
 * cycle of its own, mirroring AllowedOriginsListView; the enclosing ExtensionPackageRestAllowlistSection is
 * integrated into the shared Packages-tab Formik form, which owns the single page-level Save button.
 */
export function ExtensionPackageRestAllowlistListView(props: ExtensionPackageRestAllowlistListViewProps): React.ReactElement {
  const { entries, onChange, disabled } = props;

  const handleUrlChange = (index: number, url: string) => {
    const next = entries.slice();
    next[index] = { ...next[index], url };
    onChange(next);
  };

  const handleVerbChange = (index: number, verb: string) => {
    const next = entries.slice();
    next[index] = { ...next[index], verb };
    onChange(next);
  };

  const handleDelete = (index: number) => {
    const next = entries.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const handleAdd = () => {
    // Prefill the common path prefix so admins type less; {api-version} and {site-id} are the
    // placeholders the request-time bridge fills automatically. This is a valid entry as-is (see
    // ExtensionPackageRestAllowlistCodec), so a freshly added row shows no error until it is edited.
    onChange(entries.concat([{ url: EXTENSION_PACKAGE_REST_ALLOWLIST_PATH_PREFIX, verb: EXTENSION_PACKAGE_REST_ALLOWLIST_VERBS[0] }]));
  };

  return (
    <div data-tb-test-id={EXTENSION_PACKAGE_REST_ALLOWLIST_LIST_TEST_ID} className={listContainerStyle}>
      {entries.map((entry, index) => {
        const violation = ExtensionPackageRestAllowlistCodec.findEntryViolation(entry);
        const isValid = entry.url.trim() === '' || violation === undefined;
        return (
          <div
            key={index}
            data-tb-test-id={`${EXTENSION_PACKAGE_REST_ALLOWLIST_ROW_TEST_ID_PREFIX}-${index}`}
            className={entryRowStyle}
          >
            <div className={urlCellStyle}>
              <TextFieldWidget
                className={urlFieldStyle}
                label=''
                aria-label={Messages.extensionPackageRestAllowlistUrlLabel()}
                value={entry.url}
                valid={isValid}
                message={isValid ? '' : messageForViolation(violation)}
                placeholder={Messages.extensionPackageRestAllowlistUrlPlaceholder()}
                onChange={e => handleUrlChange(index, e.target.value)}
                disabled={disabled}
                testId={`${EXTENSION_PACKAGE_REST_ALLOWLIST_ROW_TEST_ID_PREFIX}-${index}-field`}
              />
            </div>
            <SingleSelectDropdown
              className={verbDropdownStyle}
              list={<List items={verbItems} className={dropdownListStyle} />}
              type={DropdownButtonType.Outline}
              value={entry.verb}
              onChange={(value: string) => handleVerbChange(index, value)}
              label={''}
              aria-label={Messages.extensionPackageRestAllowlistVerbLabel()}
              testId={`${EXTENSION_PACKAGE_REST_ALLOWLIST_VERB_TEST_ID_PREFIX}-${index}`}
              disabled={disabled}
            />
            <ButtonWidget
              style={{ flexShrink: 0, minWidth: 0, marginLeft: CommonStyles.Sizes.DefaultSpace, verticalAlign: 'middle' }}
              buttonType={ButtonType.Icon}
              testId={`${EXTENSION_PACKAGE_REST_ALLOWLIST_DELETE_BUTTON_TEST_ID_PREFIX}-${index}`}
              density='high'
              onClick={() => handleDelete(index)}
              disabled={disabled}
            >
              <TrashBaseIcon size={18} alt={Messages.extensionPackageRestAllowlistDeleteEntry()} />
            </ButtonWidget>
          </div>
        );
      })}
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId={EXTENSION_PACKAGE_REST_ALLOWLIST_ADD_BUTTON_TEST_ID}
        onClick={handleAdd}
        disabled={disabled}
      >
        {Messages.extensionPackageRestAllowlistAddEntry()}
      </ButtonWidget>
    </div>
  );
}
